import React from 'react'
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { format, parseISO } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { OvertimeRequest } from "@/types/supabase-types"
import { OvertimeStatus } from "@/types/enums"


// Define props for ChartView
interface ChartViewProps {
  employeeId?: string;
  startDate?: Date;
  endDate?: Date;
  searchQuery?: string;
}

interface ChartDataPoint {
  month: string;
  hours: number;
}

// Define a reason structure
interface OvertimeReason {
  main_title: string;
  reason: string | null;
}

// This type is used internally for filtering
interface OvertimeRequestWithJoinedReason extends Partial<OvertimeRequest> {
  overtime_reasons?: OvertimeReason | OvertimeReason[];
}

export function ChartView({ employeeId, startDate, endDate, searchQuery }: ChartViewProps) {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchChartData() {
      try {
        setIsLoading(true);
        
        let query = supabase
          .from('overtime_requests')
          .select(`
            date, 
            hours, 
            status, 
            approver_name, 
            approver_comment,
            overtime_reasons!justification(
              main_title,
              reason
            )
          `)
          .eq('status', OvertimeStatus.APPROVED); // Only consider approved hours
          
        // Filter by employee if provided
        if (employeeId) {
          query = query.eq('employee_id', employeeId);
        }

        // Filter by date range if provided
        if (startDate) {
          query = query.gte('date', format(startDate, 'yyyy-MM-dd'));
        }
        if (endDate) {
          query = query.lte('date', format(endDate, 'yyyy-MM-dd'));
        }
        
        const { data, error } = await query;
        
        if (error) throw error;

        // Filter data based on searchQuery *before* aggregation
        let filteredData = data || [];
        if (searchQuery) {
          const lowerCaseQuery = searchQuery.toLowerCase();
          filteredData = filteredData.filter((record: OvertimeRequestWithJoinedReason) => {
            // Handle joined overtime_reasons data
            let justificationText = '';
            if (record.overtime_reasons) {
              if (Array.isArray(record.overtime_reasons) && record.overtime_reasons.length > 0) {
                justificationText = record.overtime_reasons[0].reason || record.overtime_reasons[0].main_title || '';
              } else {
                // Single object case
                const reasonObj = record.overtime_reasons as OvertimeReason;
                justificationText = reasonObj.reason || reasonObj.main_title || '';
              }
            }
            
            return justificationText.toLowerCase().includes(lowerCaseQuery) ||
              (record.approver_name || '').toLowerCase().includes(lowerCaseQuery) ||
              (record.approver_comment || '').toLowerCase().includes(lowerCaseQuery)
              // status is already filtered to 'approved' by the query
          });
        }

        // Aggregate filtered data by month
        const monthlyData: { [key: string]: number } = {};
        filteredData.forEach((record: Partial<OvertimeRequest>) => {
          if (record.date) {
            const month = format(parseISO(record.date), 'yyyy-MM'); // Group by year-month
            monthlyData[month] = (monthlyData[month] || 0) + (record.hours || 0);
          }
        });

        // Convert to array format expected by Recharts, sorted by month
        const formattedData = Object.entries(monthlyData)
          .map(([month, hours]) => ({ month: format(parseISO(`${month}-01`), 'MMM yyyy'), hours }))
          .sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());

        setChartData(formattedData);
      } catch {
        // Error handled silently
      } finally {
        setIsLoading(false);
      }
    }

    fetchChartData();
  }, [employeeId, startDate, endDate, searchQuery]);

  if (isLoading) {
    return <Skeleton className="h-[300px] w-full" />;
  }

  if (chartData.length === 0) {
    return (
      <div className="p-4 border rounded-md min-h-[300px] flex items-center justify-center">
        <p className="text-muted-foreground">No overtime data available for the selected period.</p>
      </div>
    )
  }

  return (
    <div className="p-4 border rounded-md min-h-[300px]">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <XAxis 
            dataKey="month" 
            axisLine={true} 
            stroke="hsl(var(--muted-foreground)/40)" 
            tickLine={false}
          />
          <YAxis 
            axisLine={true} 
            stroke="hsl(var(--muted-foreground)/40)" 
            tickLine={false}
          />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="hours" stroke="#8884d8" activeDot={{ r: 8 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
